import { logError } from "@/lib/logError";
import { useCustomerAccount } from "@/providers";
import { useAppDispatch } from "@/redux/hooks";
import { enhancedApi } from "@/redux/services/enhancedApi";

/**
 * Hook that provides refresh functions for payment-related lists.
 * Exports individual refresh functions for the unconfirmed and processing lists
 * as well as a unified refreshAllLists function for when all lists need to be updated.
 *
 * Note: Uses cache invalidation instead of query subscriptions to avoid multiple
 * simultaneous subscriptions when components are mounted.
 */
export const useRefreshAllLists = () => {
  const { customerAccount } = useCustomerAccount();
  const dispatch = useAppDispatch();

  /**
   * Refreshes only the unconfirmed (pending) payments list
   */
  const refreshUnconfirmedList = () => {
    if (!customerAccount?.customerAccount?.id) {
      return;
    }
    try {
      dispatch(enhancedApi.util.invalidateTags(["PaymentLists"]));
    } catch (error) {
      logError("Error refreshing unconfirmed list", error);
    }
  };

  /**
   * Refreshes only the processing payments list
   */
  const refreshProcessingList = () => {
    if (!customerAccount?.customerAccount?.id) {
      return;
    }
    try {
      dispatch(enhancedApi.util.invalidateTags(["PaymentLists"]));
    } catch (error) {
      logError("Error refreshing processing list", error);
    }
  };

  /**
   * Refreshes all payment lists by invalidating their caches.
   * Use this when a pending payment is confirmed and items need to move between lists.
   */
  const refreshAllLists = () => {
    if (!customerAccount?.customerAccount?.id) {
      return;
    }
    try {
      // Invalidate all payment-related caches
      dispatch(enhancedApi.util.invalidateTags(["PaymentLists"]));
      dispatch(enhancedApi.util.invalidateTags(["LedgerEntries"]));
    } catch (error) {
      logError("Error refreshing lists", error);
    }
  };

  return {
    refreshUnconfirmedList,
    refreshProcessingList,
    refreshAllLists,
  };
};
